import React from "react"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import cx from "classnames"
import Label from "../label"

export type DatePickerInputProps = {
  /** HTML name attribute forwarded to the hidden input */
  name?: string
  /** Additional CSS class names applied to the date picker input element */
  className?: string
  /** Called when the user selects a date or time, with a synthetic event containing the chosen Date */
  onChange: (event: {
    target: { name: string | undefined; value: Date | null }
  }) => void
  /** Minute interval between time options in the time picker dropdown */
  timeIntervals?: number
  /** Label displayed above the input */
  label?: string
  /** Currently selected date value (controlled) */
  selected?: Date | null
  /** ID of a DOM element to render the calendar popup inside, useful for portalling out of overflow-hidden containers */
  portalId?: string
  /** HTML id attribute for the input element */
  id?: string
  /** Earliest selectable date; dates before this are disabled */
  minDate?: Date
  /** Latest selectable date; dates after this are disabled */
  maxDate?: Date
  /** Earliest selectable time of day */
  minTime?: Date
  /** Latest selectable time of day */
  maxTime?: Date
  /** When true, renders the calendar inline rather than in a popup */
  inline?: boolean
  /** Whether the time picker is shown alongside the calendar (defaults to true) */
  showTimeSelect?: boolean
  /** Date format string (defaults to "MMMM d, yyyy h:mm aa") */
  dateFormat?: string
  /**
   * Replace the default input with a custom element (e.g. a DS Input).
   * react-datepicker forwards `value`, `onChange`, `onClick`, `onFocus`, `onBlur`,
   * and a ref to this element, so whatever you pass should accept them.
   */
  customInput?: React.ReactElement
}

/**
 * A date-and-time picker input that wraps react-datepicker.
 * Supports optional labels, time intervals, min/max constraints, and an inline calendar mode.
 */
function DatePickerInput({
  name,
  className: classNameProp,
  onChange,
  timeIntervals = 15,
  label,
  selected,
  portalId,
  id,
  minDate,
  maxDate,
  minTime,
  maxTime,
  inline,
  showTimeSelect = true,
  dateFormat = "MMMM d, yyyy h:mm aa",
  customInput
}: DatePickerInputProps) {
  const handleChange = (date: Date | null) => {
    onChange({ target: { name, value: date } })
  }

  const className = cx(
    classNameProp,
    "border border-gray-200 text-sm px-2 py-1 rounded-sm focus:outline-blue"
  )

  return (
    <div>
      {label && (
        <Label as="div" className="mb-1">
          {label}
        </Label>
      )}
      <DatePicker
        showTimeSelect={showTimeSelect}
        showPopperArrow={false}
        timeFormat="h:mm aa"
        timeIntervals={timeIntervals}
        timeCaption="time"
        dateFormat={dateFormat}
        className={className}
        selected={selected}
        id={id}
        portalId={portalId}
        minDate={minDate}
        maxDate={maxDate}
        minTime={minTime}
        maxTime={maxTime}
        inline={inline}
        customInput={customInput}
        onChange={handleChange}
      />
    </div>
  )
}

export { DatePickerInput }
export default DatePickerInput
